import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api';

type Step = 'signup' | 'plan' | 'byoo-value' | 'byoo-connect' | 'soul' | 'live';
type Plan = 'starter' | 'pro' | 'scale' | null;
type Soul = 'clean' | 'ops' | 'dev' | 'byoo' | null;
type Provider = 'anthropic' | 'openrouter';

const PLAN_NAMES = {
  starter: 'Starter ($29/mo)',
  pro: 'Pro ($99/mo)',
  scale: 'Scale ($299/mo)',
};

const SOUL_NAMES: Record<string, string> = {
  clean: 'Clean & Crisp',
  ops: 'Ops Rockstar',
  dev: '10x Engineer',
  byoo: 'BYOO',
};

const SOUL_PROMPTS = {
  clean: [
    'What can you do?',
    'Help me set up a project',
    'Show me your skills',
  ],
  ops: [
    'Check my deployment status',
    'Help me write a runbook',
    'Set up monitoring',
  ],
  dev: [
    'Review my latest PR',
    'Help me debug this',
    'Architect a notification system',
  ],
  byoo: [
    'Test the connection',
    'Show me routing stats',
    'Run a benchmark',
  ],
};

export function Onboarding() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // User is already authenticated via Clerk — skip signup step
  const [currentStep, setCurrentStep] = useState<Step>('plan');
  const [selectedPlan, setSelectedPlan] = useState<Plan>(null);
  const [selectedSoul, setSelectedSoul] = useState<Soul>(null);
  const [providerType, setProviderType] = useState<Provider>('anthropic');
  const [isBYOOFlow, setIsBYOOFlow] = useState(false);
  const [preSelectedPlan, setPreSelectedPlan] = useState<Plan>(null);
  const [apiKey, setApiKey] = useState('');
  const [proxyUrl, setProxyUrl] = useState('');
  const [configCode, setConfigCode] = useState('');
  const [showProxyResult, setShowProxyResult] = useState(false);
  const [copiedConfig, setCopiedConfig] = useState(false);
  const [instanceName, setInstanceName] = useState('');
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState('');

  // Initialize from URL params
  useEffect(() => {
    const planParam = searchParams.get('plan') as Plan;
    if (planParam && PLAN_NAMES[planParam]) {
      setSelectedPlan(planParam);
      setPreSelectedPlan(planParam);
      setCurrentStep('soul');
    }
  }, [searchParams]);

  // Get total steps for progress calculation
  const getTotalSteps = () => {
    if (isBYOOFlow) return 2; // Signup -> BYOO connect/value -> Live
    if (preSelectedPlan) return 3; // Signup -> Soul -> Live
    return 4; // Signup -> Plan -> Soul -> Live
  };

  // Get step labels
  const getStepLabels = () => {
    if (isBYOOFlow) return ['Sign Up', 'Connect'];
    if (preSelectedPlan) return ['Sign Up', 'Choose Soul', "You're Live"];
    return ['Sign Up', 'Choose Plan', 'Choose Soul', "You're Live"];
  };

  // Get current step index
  const getCurrentStepIndex = () => {
    if (isBYOOFlow) {
      if (currentStep === 'signup') return 0;
      if (currentStep === 'byoo-value' || currentStep === 'byoo-connect') return 1;
      if (currentStep === 'live') return 2;
    } else if (preSelectedPlan) {
      if (currentStep === 'signup') return 0;
      if (currentStep === 'soul') return 1;
      if (currentStep === 'live') return 2;
    } else {
      if (currentStep === 'signup') return 0;
      if (currentStep === 'plan') return 1;
      if (currentStep === 'soul') return 2;
      if (currentStep === 'live') return 3;
    }
    return 0;
  };

  // Progress bar renderer
  const renderProgressBar = () => {
    const labels = getStepLabels();
    const currentIndex = getCurrentStepIndex();
    const progressPercent = currentIndex === 0 ? 0 : (currentIndex / (labels.length - 1)) * 100;

    return (
      <div className="progress-container">
        <div className="progress-track">
          <div className="progress-line" />
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          {labels.map((label, index) => (
            <div key={index} className="progress-step-ob">
              <div
                className={`progress-dot ${
                  index < currentIndex ? 'completed' : index === currentIndex ? 'current' : ''
                }`}
              >
                {index < currentIndex ? '✓' : index + 1}
              </div>
              <div className={`progress-label-ob ${index === currentIndex ? 'active' : ''}`}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handlers
  const handleSignupNext = () => {
    if (preSelectedPlan) {
      setCurrentStep('soul');
    } else {
      setCurrentStep('plan');
    }
  };

  const showBYOO = () => {
    setIsBYOOFlow(true);
    setCurrentStep('byoo-value');
  };

  const showBYOOConnect = () => {
    setCurrentStep('byoo-connect');
  };

  const selectPlan = (plan: Plan) => {
    setSelectedPlan(plan);
    setCurrentStep('soul');
  };

  const selectSoul = (soul: Soul) => {
    setSelectedSoul(soul);
  };

  const toggleProvider = (provider: Provider) => {
    setProviderType(provider);
  };

  const validateBYOO = () => {
    const generatedProxyUrl = 'clz-proxy-a8f2b91c.clawzempic.com';
    const generatedConfigCode =
      providerType === 'anthropic'
        ? `ANTHROPIC_BASE_URL=https://${generatedProxyUrl}/v1`
        : `OPENROUTER_BASE_URL=https://${generatedProxyUrl}/v1`;

    setProxyUrl(generatedProxyUrl);
    setConfigCode(generatedConfigCode);
    setShowProxyResult(true);
    setSelectedSoul('byoo' as any);
  };

  const copyConfig = () => {
    navigator.clipboard.writeText(configCode).then(() => {
      setCopiedConfig(true);
      setTimeout(() => setCopiedConfig(false), 2000);
    });
  };

  const goToLive = () => {
    setCurrentStep('live');
  };

  const handleDeploy = async () => {
    if (!instanceName || !selectedSoul || isDeploying) return;
    setIsDeploying(true);
    setDeployError('');
    try {
      const result = await api.deploy(instanceName, selectedSoul === 'byoo' ? 'dev' : selectedSoul);
      setApiKey(result.apiKey);
      setProxyUrl(result.proxyUrl);
      setCurrentStep('live');
    } catch (err: any) {
      setDeployError(err.message || 'Deploy failed');
    } finally {
      setIsDeploying(false);
    }
  };

  // Confetti animation
  useEffect(() => {
    if (currentStep === 'live') {
      const confettiContainer = document.createElement('div');
      confettiContainer.className = 'confetti-container';
      document.body.appendChild(confettiContainer);

      const colors = ['#FF6B5A', '#34D399', '#F59E0B', '#38BDF8', '#D4C5A9'];

      for (let i = 0; i < 40; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = Math.random() * 2 + 's';
        confetti.style.animationDuration = Math.random() * 3 + 2 + 's';
        confetti.style.animation = 'confetti-fall linear forwards';
        confettiContainer.appendChild(confetti);
      }

      setTimeout(() => confettiContainer.remove(), 6000);
    }
  }, [currentStep]);

  return (
    <div className="container" style={{ maxWidth: '560px', margin: '0 auto', padding: '2rem 1rem', minHeight: '100vh' }}>
      {/* STEP 1: Sign Up */}
      {currentStep === 'signup' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <div className="card onboarding-card">
            <div className="centered" style={{ marginBottom: '1.5rem' }}>
              {preSelectedPlan && (
                <div className="plan-badge-header">{PLAN_NAMES[preSelectedPlan]}</div>
              )}
              <h2>Start Your Transformation</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                Your OpenClaw bill is about to get a lot lighter.
              </p>
            </div>

            <button className="button ob-button-google" onClick={handleSignupNext} style={{ marginBottom: '0.75rem' }}>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="divider">or</div>

            <div className="form-group">
              <label className="form-label">Work email</label>
              <input type="email" className="form-input ob-form-input" placeholder="you@company.com" />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input type="password" className="form-input ob-form-input" placeholder="8+ characters" />
            </div>

            <button className="button ob-button-primary" onClick={handleSignupNext}>
              Create Account
            </button>

            <a href="#" className="text-link">
              Already have an account? Sign in
            </a>
          </div>

          <div className="byoo-link">
            Already running OpenClaw?{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); showBYOO(); }}>
              Connect your existing instance
            </a>
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}

      {/* STEP 2: Choose Plan */}
      {currentStep === 'plan' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <div className="card onboarding-card">
            <div className="centered" style={{ marginBottom: '2rem' }}>
              <h2>Choose your plan</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                All plans include routing, caching, compression, and scripts.
              </p>
            </div>

            <div className="ob-plan-card" onClick={() => selectPlan('starter')}>
              <div className="plan-header">
                <div>
                  <span className="badge badge-blue">STARTER</span>
                  <div className="plan-name">Starter</div>
                </div>
                <div className="plan-price">
                  $29<span>/mo</span>
                </div>
              </div>
              <div className="plan-tagline">Side projects that deserve to survive.</div>
              <div className="plan-inference">$25/mo inference included</div>
              <button className="button ob-button-primary">Select</button>
            </div>

            <div className="ob-plan-card recommended" onClick={() => selectPlan('pro')}>
              <div className="plan-header">
                <div>
                  <span className="badge badge-green">PRO</span>
                  <div className="plan-name">Pro</div>
                </div>
                <div className="plan-price">
                  $99<span>/mo</span>
                </div>
              </div>
              <div className="plan-tagline">Teams shipping real products.</div>
              <div className="plan-inference">$100/mo inference included</div>
              <button className="button ob-button-green">Select</button>
            </div>

            <div className="ob-plan-card" onClick={() => selectPlan('scale')}>
              <div className="plan-header">
                <div>
                  <span className="badge badge-gold">SCALE</span>
                  <div className="plan-name">Scale</div>
                </div>
                <div className="plan-price">
                  $299<span>/mo</span>
                </div>
              </div>
              <div className="plan-tagline">Agents that never sleep.</div>
              <div className="plan-inference">$500/mo inference included</div>
              <button className="button ob-button-primary">Select</button>
            </div>

            <div className="byoo-link" style={{ marginTop: '1.5rem' }}>
              Have your own OpenClaw?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); showBYOO(); }}>
                Get BYOO ($49/mo flat)
              </a>
            </div>
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}

      {/* STEP BYOO VALUE */}
      {currentStep === 'byoo-value' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <div className="card onboarding-card">
            <div className="centered" style={{ marginBottom: '2rem' }}>
              <h2>Supercharge Your Setup</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                You already have OpenClaw. Here's what you're about to unlock:
              </p>
            </div>

            <div className="value-list">
              <div className="value-item">
                <span className="value-check">✓</span>
                <div>
                  <div className="value-title">Intelligent Model Routing</div>
                  <div className="value-desc">Save 90%+ by auto-routing simple tasks to Haiku</div>
                </div>
              </div>
              <div className="value-item">
                <span className="value-check">✓</span>
                <div>
                  <div className="value-title">Prompt Caching</div>
                  <div className="value-desc">Cut repeat requests to nearly zero</div>
                </div>
              </div>
              <div className="value-item">
                <span className="value-check">✓</span>
                <div>
                  <div className="value-title">Scripts Library</div>
                  <div className="value-desc">Free automations that skip the LLM entirely</div>
                </div>
              </div>
              <div className="value-item">
                <span className="value-check">✓</span>
                <div>
                  <div className="value-title">Skills Marketplace</div>
                  <div className="value-desc">Community extensions for your agent</div>
                </div>
              </div>
              <div className="value-item">
                <span className="value-check">✓</span>
                <div>
                  <div className="value-title">Usage Dashboard</div>
                  <div className="value-desc">See exactly where your money goes</div>
                </div>
              </div>
            </div>

            <button className="button ob-button-primary" onClick={showBYOOConnect}>
              Ready to Connect
            </button>
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}

      {/* STEP BYOO CONNECT */}
      {currentStep === 'byoo-connect' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <div className="card onboarding-card">
            <div className="centered" style={{ marginBottom: '2rem' }}>
              <h2>Connect your OpenClaw</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                Paste your API key. We'll handle the rest.
              </p>
            </div>

            <div className="ob-toggle-pills">
              <button
                className={`toggle-pill ${providerType === 'anthropic' ? 'active' : ''}`}
                onClick={() => toggleProvider('anthropic')}
              >
                Anthropic
              </button>
              <button
                className={`toggle-pill ${providerType === 'openrouter' ? 'active' : ''}`}
                onClick={() => toggleProvider('openrouter')}
              >
                OpenRouter
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Your API Key</label>
              <input
                type="text"
                className="form-input ob-form-input mono"
                placeholder={providerType === 'anthropic' ? 'sk-ant-...' : 'sk-or-...'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            <button className="button ob-button-primary" onClick={validateBYOO}>
              Validate & Connect
            </button>

            {showProxyResult && (
              <div id="proxy-result">
                <div className="proxy-url">{proxyUrl}</div>

                <div className="code-block">
                  <button
                    className={`copy-button ${copiedConfig ? 'copied' : ''}`}
                    onClick={copyConfig}
                  >
                    {copiedConfig ? 'Copied!' : 'Copy'}
                  </button>
                  <code>{configCode}</code>
                </div>

                <button className="button ob-button-green" onClick={goToLive} style={{ marginTop: '1rem' }}>
                  Continue to Dashboard
                </button>
              </div>
            )}
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}

      {/* STEP 3: Choose Soul */}
      {currentStep === 'soul' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <div className="card onboarding-card">
            <div className="centered" style={{ marginBottom: '2rem' }}>
              <h2>Choose your soul</h2>
              <p className="subtitle" style={{ marginBottom: 0 }}>
                Pick a personality. You can always change it later.
              </p>
            </div>

            {selectedPlan && (
              <div className="plan-status">
                <span className="plan-current">You're on the {PLAN_NAMES[selectedPlan]} plan</span>
                <a
                  href="#"
                  className="plan-change"
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentStep('plan');
                  }}
                >
                  Change plan
                </a>
              </div>
            )}

            <div className="ob-soul-card" onClick={() => selectSoul('clean')}>
              <div className="soul-header">
                <span className="badge badge-muted">RAW</span>
                <div className="soul-title">Clean & Crisp</div>
              </div>
              <div className="soul-description">No opinions. No personality. Full control.</div>
              <div className="soul-meta">
                <div>Skills: Base capabilities only</div>
                <div>Best for: Power users</div>
              </div>
            </div>

            <div className="ob-soul-card" onClick={() => selectSoul('ops')}>
              <div className="soul-header">
                <span className="badge badge-coral">OPS</span>
                <div className="soul-title">Ops Rockstar</div>
              </div>
              <div className="soul-description">Monitoring, deployment, incident response.</div>
              <div className="soul-meta">
                <div>Skills: CI/CD, alerting, runbooks</div>
                <div>Best for: DevOps & SREs</div>
              </div>
            </div>

            <div className="ob-soul-card" onClick={() => selectSoul('dev')}>
              <div className="soul-header">
                <span className="badge badge-green">DEV</span>
                <div className="soul-title">10x Engineer</div>
              </div>
              <div className="soul-description">Code review, debugging, architecture.</div>
              <div className="soul-meta">
                <div>Skills: Git, testing, code analysis</div>
                <div>Best for: Software engineers</div>
              </div>
            </div>

            {selectedSoul && (
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label className="form-label">Instance Name</label>
                <input
                  type="text"
                  className="form-input ob-form-input mono"
                  placeholder="my-agent"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  disabled={isDeploying}
                />
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                  3-40 chars, lowercase, hyphens OK. This becomes your subdomain.
                </div>
              </div>
            )}

            {deployError && (
              <div style={{ color: '#FF6B5A', fontSize: '0.875rem', marginTop: '0.75rem' }}>
                {deployError}
              </div>
            )}

            <button
              className="button ob-button-green"
              onClick={handleDeploy}
              disabled={!selectedSoul || !instanceName || instanceName.length < 3 || isDeploying}
              style={{ marginTop: '1rem', opacity: (selectedSoul && instanceName.length >= 3 && !isDeploying) ? 1 : 0.5 }}
            >
              {isDeploying ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                  <span className="deploy-spinner" style={{ width: '16px', height: '16px' }} />
                  Deploying...
                </span>
              ) : (
                selectedSoul ? `Deploy with ${SOUL_NAMES[selectedSoul]}` : 'Deploy'
              )}
            </button>
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}

      {/* STEP 4: You're Live */}
      {currentStep === 'live' && (
        <div className="step active">
          <div className="onboarding-logo">
            <div className="onboarding-logo-text">CLAWZEMPIC</div>
            <div className="onboarding-logo-sub">The Inference Diet</div>
          </div>

          {renderProgressBar()}

          <img src="/after-clawzempic.png" alt="Slim lobster" className="success-lobster" />

          <div className="centered" style={{ marginBottom: '1.5rem' }}>
            <h2>You're live. Say hello.</h2>
            {selectedSoul === 'byoo' ? (
              <p className="subtitle" style={{ marginBottom: 0 }}>
                Your OpenClaw is now supercharged with Clawzempic. Your existing configuration, skills, and personality are preserved. We're just the smart proxy layer.
              </p>
            ) : (
              <p className="subtitle" style={{ marginBottom: 0 }}></p>
            )}
          </div>

          {proxyUrl && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#888', marginBottom: '0.25rem' }}>Proxy URL</div>
              <div className="code-block"><code>{proxyUrl}</code></div>
              {apiKey && (
                <>
                  <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#888', marginBottom: '0.25rem', marginTop: '0.75rem' }}>API Key</div>
                  <div className="code-block"><code>{apiKey}</code></div>
                </>
              )}
            </div>
          )}

          <div id="prompts-container">
            {selectedSoul && SOUL_PROMPTS[selectedSoul as keyof typeof SOUL_PROMPTS].map((prompt) => (
              <div key={prompt} className="prompt-card">
                {prompt}
              </div>
            ))}
          </div>

          <div className="buttons-row">
            <Link to="/chat" className="button ob-button-green">
              Open Chat
            </Link>
            <Link to="/dashboard" className="button ob-button-secondary">
              Open Dashboard
            </Link>
          </div>

          <div className="minimal-footer">
            <a href="#">Privacy Policy</a>
            <span className="separator">&middot;</span>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      )}
    </div>
  );
}
